import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Ticket, Activity, HardDrive, BookOpen, Settings as SettingsIcon, CreditCard, Shield, LogOut, Menu, X, ChevronDown, FileSignature, FileCheck, Bell, Server, Users, Globe } from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = ({ user, onLogout, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuItems = [
    {
      id: 'clients',
      label: 'Clients',
      icon: Building2,
      path: '/companies',
      active: true,
      subItems: [
        { label: 'All Clients', path: '/companies' },
        { label: 'Add New', path: '/new-companies' },
        { label: 'New Email', path: '/compose-email' }
      ]
    },
    {
      id: 'prospects',
      label: 'Prospects',
      icon: Users,
      path: '/prospects',
      active: true,
      subItems: [
        { label: 'All Prospects', path: '/prospects' }
      ]
    },
    {
      id: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      path: '/tickets',
      active: true,
      subItems: [
        { label: 'All Tickets', path: '/tickets' },
        { label: 'Assigned to Me', path: '/tickets?filter=assigned_to_me' },
        { label: 'Unassigned', path: '/tickets?filter=unassigned' }
      ]
    },
    {
      id: 'uptime',
      label: 'Uptime Monitor',
      icon: Activity,
      path: '/uptime',
      active: true,
      subItems: [
        { label: 'All Monitors', path: '/uptime' }
      ]
    },
    {
      id: 'hypervisors',
      label: 'Hypervisors',
      icon: Server,
      path: '/hypervisors',
      active: true,
      subItems: [
        { label: 'All Hypervisors', path: '/hypervisors' }
      ]
    },
    {
      id: 'assets',
      label: 'Assets',
      icon: HardDrive,
      path: '/assets',
      active: true,
      subItems: [
        { label: 'Managed Agents', path: '/assets' },
        { label: 'Network Devices', path: '/network-assets' }
      ]
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: Bell,
      path: '/alerts',
      active: true,
      subItems: [
        { label: 'All Alerts', path: '/alerts' }
      ]
    },
    {
      id: 'knowledge',
      label: 'Knowledge Base',
      icon: BookOpen,
      path: '/knowledge',
      active: true,
      subItems: [
        { label: 'All Articles', path: '/knowledge' }
      ]
    },
    {
      id: 'contracts',
      label: 'Contracts',
      icon: FileSignature,
      path: '/contracts',
      active: true,
      subItems: [
        { label: 'All Contracts', path: '/contracts' },
        { label: 'Templates', path: '/contracts/templates' }
      ]
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: FileCheck,
      path: '/requests',
      active: true,
      subItems: [
        { label: 'All Requests', path: '/requests' },
        { label: 'New Request', path: '/requests/new' }
      ]
    }
  ];

  const bottomItems = [
    {
      id: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      path: '/settings',
      subItems: [
        { label: 'General', path: '/settings' },
        { label: 'Users', path: '/settings/users' },
        { label: 'Agent Installer', path: '/settings/agent-installer' },
        { label: 'Integrations', path: '/settings/integrations' },
        { label: 'Client Configuration', path: '/settings/client-configuration' },
        { label: 'Screenshots', path: '/settings/screenshots' },
        { label: 'Asset Alerts', path: '/settings/asset-alerts' },
        { label: 'Monitor Alerts', path: '/settings/monitor-alerts' },
        { label: 'Pricing', path: '/settings/pricing' },
        { label: 'Terms & Conditions', path: '/settings/terms' }
      ]
    },
    ...(user?.role === 'admin' ? [
      { id: 'admin', label: 'Admin', icon: Shield, path: '/admin' },
      { id: 'platform-admin', label: 'Platform Admin', icon: Globe, path: '/platform-admin' }
    ] : [])
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className="bg-white shadow-md"
        >
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full bg-slate-900 text-white transition-all duration-300 z-40 flex flex-col ${
          collapsed ? '-translate-x-full lg:translate-x-0 lg:w-20' : 'w-64'
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="text-lg font-bold">BitzUK Portal</h1>
                <p className="text-xs text-slate-400">Client Management</p>
              </div>
            )}
          </div>
        </div>

        {/* User Info */}
        {!collapsed && (
          <div className="p-4 border-b border-slate-700 bg-slate-800 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-semibold text-purple-400">{user?.credits || 0} Credits</span>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/purchase-credits')}
                className="bg-purple-600 hover:bg-purple-700 h-7 text-xs"
              >
                Buy
              </Button>
            </div>
          </div>
        )}

        {/* Navigation - Scrollable Area */}
        <nav className="flex-1 overflow-y-auto py-4 min-h-0 custom-scrollbar">
          <div className="space-y-1 px-3">
            {menuItems.map((item) => (
              <div key={item.id}>
                <button
                  onClick={() => {
                    if (item.active) {
                      if (item.subItems) {
                        toggleSection(item.id);
                      } else {
                        navigate(item.path);
                      }
                    }
                  }}
                  disabled={!item.active}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : item.active
                      ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      : 'text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </div>
                  {!collapsed && (
                    <div className="flex items-center space-x-2">
                      {item.badge && (
                        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                          {item.badge}
                        </span>
                      )}
                      {item.subItems && item.active && (
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            expandedSections[item.id] ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </div>
                  )}
                </button>

                {/* Sub-items */}
                {!collapsed && item.subItems && expandedSections[item.id] && item.active && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.subItems.map((subItem, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(subItem.path)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          isActive(subItem.path)
                            ? 'bg-slate-800 text-blue-400'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Bottom Items - Fixed at bottom */}
        <div className="flex-shrink-0 pt-4 border-t border-slate-700 space-y-1 px-3 pb-4 bg-slate-900">
            {bottomItems.map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={() => {
                    if (item.subItems) {
                      toggleSection(item.id);
                    } else {
                      navigate(item.path);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </div>
                  {!collapsed && item.subItems && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        expandedSections[item.id] ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </button>

                {/* Sub-items for Settings */}
                {!collapsed && item.subItems && expandedSections[item.id] && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.subItems.map((subItem, subIdx) => (
                      <button
                        key={subIdx}
                        onClick={() => navigate(subItem.path)}
                        className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          isActive(subItem.path)
                            ? 'bg-slate-800 text-blue-400'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Overlay for mobile */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}
    </>
  );
};

export default Sidebar;
